import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Building2, Check, Eye, EyeOff, Loader2,
  Lock, Mail, ShieldCheck, User,
} from 'lucide-react';
import { AuthShell } from './AuthShell';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';

/**
 * Trainer registration — same visual standard as LoginPage:
 *   • brand-blue left panel with circles + AI illustration + pillars
 *   • white card with eyebrow + Instrument-Serif title + icon square
 *   • 46-px icon-prefixed inputs, gradient submit
 *
 * Polish additions for the register flow specifically:
 *   • Grouped sections ("Your details" / "Credentials") with quiet
 *     section labels — gives 4 fields a calm rhythm.
 *   • Live email validity check — green tick appears on the right of
 *     the field when the regex passes.
 *   • Password strength meter under the password field — instant
 *     visual feedback as the trainer types.
 *   • Terms checkbox styled to match the rest of the surface.
 */
const RegisterPage: React.FC = () => {
  const { register, busy, error, clearError } = useAiTrainerAuth();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    organization: '',
  });

  /* ── Helpers ──────────────────────────────────────────────── */
  const emailValid = useMemo(
    () => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()),
    [form.email],
  );

  const pwStrength = useMemo(() => scorePassword(form.password), [form.password]);

  const canSubmit =
    form.full_name.trim().length >= 2 &&
    emailValid &&
    form.password.length >= 8 &&
    acceptTerms &&
    !busy;

  const onChange = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((p) => ({ ...p, [k]: e.target.value }));
      if (error) clearError();
    };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const user = await register({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        organization: form.organization.trim() || undefined,
      });
      navigate(user.status === 'active' ? '/library' : '/pending', { replace: true });
    } catch { /* surfaced via context */ }
  };

  return (
    <AuthShell
      eyebrow="Become a trainer"
      title="Create your account"
      subtitle="Takes under a minute — passwords are bcrypt-hashed on the server"
      icon={<ShieldCheck size={18} />}
      iconAccent
      brandHeadline={
        <>
          Join the trainers<br />
          <em className="not-italic italic text-[#d4f0e9]/90">teaching the AI</em>
        </>
      }
      brandSub="Self-register, upload past papers, and watch the institution's question-generation model improve every week."
      footer={
        <p className="m-0 text-[13px] text-slate-500">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-semibold text-[#0e9172] transition hover:text-[#0a7a5e] hover:underline"
          >
            Sign in
          </Link>
        </p>
      }
    >
      <form className="flex flex-col gap-6" onSubmit={submit} noValidate>

        {/* ── Section 1: Your details ── */}
        <SectionLabel>Your details</SectionLabel>
        <div className="flex flex-col gap-4">
          <Field label="Full name" icon={<User size={14} />}>
            <InputWrap leftIcon={<User size={16} />}>
              <input
                type="text"
                required
                autoComplete="name"
                placeholder="Jane Examiner"
                value={form.full_name}
                onChange={onChange('full_name')}
                className={inputCls(!!error)}
                autoFocus
              />
            </InputWrap>
          </Field>

          <Field
            label="Work email"
            icon={<Mail size={14} />}
            aside={
              emailValid && (
                <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#0e9172]">
                  <Check size={12} /> looks valid
                </span>
              )
            }
          >
            <InputWrap leftIcon={<Mail size={16} />}>
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="trainer@institution.ac.ke"
                value={form.email}
                onChange={onChange('email')}
                className={inputCls(!!error)}
              />
              {emailValid && (
                <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[#0e9172]">
                  <Check size={16} />
                </span>
              )}
            </InputWrap>
          </Field>

          <Field label="Organization (optional)" icon={<Building2 size={14} />}>
            <InputWrap leftIcon={<Building2 size={16} />}>
              <input
                type="text"
                autoComplete="organization"
                placeholder="e.g. Sikri TVC"
                value={form.organization}
                onChange={onChange('organization')}
                className={inputCls(!!error)}
              />
            </InputWrap>
          </Field>
        </div>

        {/* ── Section 2: Credentials ── */}
        <SectionLabel>Credentials</SectionLabel>
        <div className="flex flex-col gap-1.5">
          <Field label="Password" icon={<Lock size={14} />}>
            <InputWrap leftIcon={<Lock size={16} />}>
              <input
                type={showPw ? 'text' : 'password'}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
                value={form.password}
                onChange={onChange('password')}
                className={`${inputCls(!!error)} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                tabIndex={-1}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 border-0 bg-transparent p-0 text-slate-400 transition hover:text-slate-600"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </InputWrap>
          </Field>

          <PasswordStrength password={form.password} score={pwStrength} />
        </div>

        {/* ── Terms acceptance ── */}
        <label className="flex cursor-pointer select-none items-start gap-3 rounded-[10px] border border-slate-200 bg-slate-50 px-3.5 py-3 transition hover:border-slate-300">
          <span
            className={`
              mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-[1.5px] transition
              ${acceptTerms
                ? 'border-[#0e9172] bg-[#0e9172] text-white'
                : 'border-slate-300 bg-white text-transparent'}
            `}
          >
            <Check size={13} strokeWidth={3} />
          </span>
          <input
            type="checkbox"
            className="sr-only"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          <span className="text-[13px] leading-relaxed text-slate-600">
            I agree to the{' '}
            <a href="/legal/terms" className="font-semibold text-[#1a5276] hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/legal/privacy" className="font-semibold text-[#1a5276] hover:underline">
              Privacy Policy
            </a>
            . Uploaded papers are used only to train your institution's
            self-hosted model.
          </span>
        </label>

        {/* Error pill */}
        {error && (
          <div className="flex items-start gap-2.5 rounded-[10px] border border-[#c0392b]/20 bg-[#c0392b]/[0.08] px-4 py-3 text-[13.5px] text-[#c0392b]">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="mt-1 flex h-[46px] w-full items-center justify-center gap-2 rounded-[10px] bg-gradient-to-br from-[#0e9172] to-[#0a7a5e] text-[14px] font-semibold text-white transition hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(14,145,114,0.3)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          {busy
            ? <><Loader2 size={16} className="animate-spin" /> Creating account…</>
            : <>Create account <ArrowRight size={16} /></>}
        </button>
      </form>
    </AuthShell>
  );
};

/* ── Atoms ───────────────────────────────────────────────── */

const inputCls = (hasError: boolean): string => `
  block h-[46px] w-full rounded-[10px] border-[1.5px] bg-white pl-[42px] pr-3.5
  text-[14px] font-normal text-slate-900 outline-none
  transition focus:border-[#1a5276] focus:ring-[3px] focus:ring-[#1a5276]/10
  ${hasError ? 'border-[#c0392b] focus:border-[#c0392b] focus:ring-[#c0392b]/10' : 'border-slate-200'}
`.replace(/\s+/g, ' ').trim();

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-2 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-slate-400">
    <span>{children}</span>
    <span className="h-px flex-1 bg-slate-200" />
  </div>
);

const Field: React.FC<{
  label: string;
  icon: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, icon, aside, children }) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
        <span className="text-slate-400">{icon}</span>
        {label}
      </label>
      {aside}
    </div>
    {children}
  </div>
);

const InputWrap: React.FC<{ leftIcon: React.ReactNode; children: React.ReactNode }> = ({ leftIcon, children }) => (
  <div className="group relative">
    <span className="pointer-events-none absolute left-3.5 top-1/2 flex -translate-y-1/2 text-slate-400 transition group-focus-within:text-[#1a5276]">
      {leftIcon}
    </span>
    {children}
  </div>
);

/* ── Password strength meter ─────────────────────────────── */

interface Strength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  hint: string;
  tone: string;     // tailwind colour token
}

function scorePassword(pw: string): Strength {
  if (!pw) return { score: 0, label: '', hint: 'Use 8 or more characters.', tone: 'bg-slate-200' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  // Clamp into 0–4 buckets so the bar's quarter widths stay clean.
  const score = Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
  const meta = {
    0: { label: '',         hint: 'Use 8 or more characters.',                tone: 'bg-slate-200' },
    1: { label: 'Weak',     hint: 'Add length — 12+ characters is safer.',    tone: 'bg-[#c0392b]' },
    2: { label: 'Okay',     hint: 'Mix upper, lower, numbers.',                tone: 'bg-[#d97706]' },
    3: { label: 'Good',     hint: 'Add a symbol for extra strength.',          tone: 'bg-[#b78600]' },
    4: { label: 'Strong',   hint: 'Nice — that\'s a solid passphrase.',       tone: 'bg-[#0e9172]' },
  }[score];
  return { score, ...meta };
}

const PasswordStrength: React.FC<{ password: string; score: Strength }> = ({ password, score }) => {
  if (!password) {
    return (
      <p className="m-0 mt-1 text-[12px] text-slate-400">
        {score.hint}
      </p>
    );
  }
  return (
    <div className="mt-1.5">
      <div className="flex items-center justify-between text-[11.5px]">
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((step) => (
            <span
              key={step}
              className={`
                h-[3px] w-8 rounded-full transition-all duration-300
                ${step <= score.score ? score.tone : 'bg-slate-200'}
              `}
            />
          ))}
        </div>
        {score.label && (
          <span
            className={`
              font-bold uppercase tracking-wider
              ${score.score >= 4 ? 'text-[#0e9172]'
                : score.score === 3 ? 'text-[#b78600]'
                : score.score === 2 ? 'text-[#d97706]'
                : 'text-[#c0392b]'}
            `}
          >
            {score.label}
          </span>
        )}
      </div>
      <p className="m-0 mt-1.5 text-[12px] text-slate-400">{score.hint}</p>
    </div>
  );
};

export default RegisterPage;
